import { useCallback, useEffect, useState } from "react";
import { Check, Loader } from "lucide-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import SearchInput from "@/components/common/SearchInput";
import cropSettingsApi from "@/api/crop-settings";
import { Actions, Mode, ProductTypes, Name, Preview } from "./Details";
import { CropSettings } from "@/interfaces/crop-settings";
import { useArtwork } from "@/providers/ArtworkProvider";
import templateApi from "@/api/template";

interface Props {
  header?: string;
  onChange?: (item?: CropSettings) => void;
  onSelect?: (item: CropSettings) => void;
}

export default function SettingsList({ header, onChange, onSelect }: Props) {
  const [itemList, setItemList] = useState<CropSettings[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [selected, setSelected] = useState<CropSettings>();
  const selectTemplates = useArtwork().selectTemplates;

  const onPageChange = (event: any) => {
    setPage(event.page + 1);
    setSize(event.rows);
  };

  const getItemList = useCallback(() => {
    setLoading(true);
    cropSettingsApi
      .getMany({
        page,
        size,
        search,
      })
      .then((data) => {
        setItemList(data.data);
        const pagination = data.pagination;
        if (pagination) setTotalItems(pagination.totalItems);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, page, size]);

  useEffect(() => {
    getItemList();
  }, [getItemList]);

  useEffect(() => {
    onChange?.(selected);
  }, [onChange, selected]);

  return (
    <>
      <div className="flex h-full flex-col overflow-auto">
        <div className="flex items-center justify-between gap-4 border-b-1 border-gray-300 p-4">
          <div className="flex flex-1 items-center gap-2">
            <span className="text-md truncate font-bold">{header}</span>
          </div>
          {loading && <Loader className="animate-spin" size={20} />}
          <div className="flex items-center gap-2">
            <SearchInput
              onChange={() => setLoading(true)}
              onSearch={setSearch}
            />
          </div>
        </div>
        <DataTable
          rowHover
          className="data-table h-full overflow-auto"
          value={itemList}
          size="small"
          scrollable
          selectionMode="single"
          selection={selected}
          onSelectionChange={(event) =>
            setSelected(event.value as CropSettings)
          }
        >
          <Column
            field="name"
            header="Name"
            body={(item) => <Name data={item} />}
          ></Column>
          {/* <Column
            field="mode"
            header="Mode"
            body={(item) => <Mode data={item} />}
          ></Column> */}
          <Column
            field="productTypes"
            header="Product types"
            body={(item) => <ProductTypes data={item} />}
          ></Column>
          <Column
            field="preview"
            header="Preview"
            body={(item) => <Preview data={item} />}
          ></Column>
          {!onSelect && (
            <Column
              headerClassName="w-28 max-w-28 min-w-28"
              className="w-28 max-w-28 min-w-28"
              frozen
              alignFrozen="right"
              field="actions"
              header="Actions"
              body={(item) => (
                <Actions
                  data={item}
                  onAdd={async () => {
                    const templates = await selectTemplates();
                    if (templates?.length) {
                      const payload = templates.map((t) => ({
                        _id: t._id,
                        cropSettingsId: item._id,
                      }));
                      await templateApi.updateMany(payload);
                      setSelected({ ...item });
                    }
                  }}
                />
              )}
            ></Column>
          )}
          {onSelect && (
            <Column
              headerClassName="w-28 max-w-28 min-w-28"
              className="w-28 max-w-28 min-w-28"
              style={{ borderLeft: "1px solid #e5e7eb" }}
              frozen
              alignFrozen="right"
              body={(item) => (
                <div className="flex justify-center">
                  <Button
                    severity="info"
                    style={{
                      height: 36,
                      margin: 0,
                      padding: "6px 10px",
                      gap: 4,
                    }}
                    label="Select"
                    icon={<Check size={18} />}
                    onClick={() => onSelect(item)}
                  />
                </div>
              )}
            ></Column>
          )}
        </DataTable>
        <div className="pagination flex w-full justify-end border-t-1 border-gray-300">
          <Paginator
            first={size * (page - 1)}
            rows={size}
            totalRecords={totalItems}
            // rowsPerPageOptions={[10, 20, 50]}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </>
  );
}
